import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Brand } from '@hygh-vybe/lib';
import { SEO } from '../components/SEO';
import { LegalizationSection } from '../components/LegalizationSection';
import './HomePage.css';
import { API_BASE } from '../config';

export function HomePage() {
  const [featuredBrands, setFeaturedBrands] = useState<Brand[]>([]);

  useEffect(() => {
    async function fetchFeaturedBrands() {
      try {
        const res = await fetch(`${API_BASE}/v1/brands`);
        if (!res.ok) return;
        const json = await res.json();
        const brands = (json.data || []).filter(
          (b: Brand) => b.featured,
        );
        setFeaturedBrands(brands);
      } catch {
        // Non-critical
      }
    }
    fetchFeaturedBrands();
  }, []);

  return (
    <div className="home-page">
      <SEO
        title="Buy THC Drinks & Hemp Beverages Direct from Brands"
        description="Discover, rate, review, and purchase THC drinks and hemp beverages directly from source brands. Find the best THC seltzers, sodas, and tonics — rated by real people."
        canonical="/"
      />
      {/* Hero with logo */}
      <section className="home-page__hero">
        <img
          src="/images/hyghvybe-logo.png"
          alt="Hygh Vybe — Find your vibe, drink with us"
          className="home-page__logo"
        />
        <p className="home-page__subtitle">The go-to platform for discovering, rating, reviewing, and purchasing THC beverages directly from the source brands.</p>
        <div className="home-page__cta-row">
          <Link to="/thc-drinks" className="home-page__cta home-page__cta--primary">
            Explore Drinks
          </Link>
          <Link to="/thc-drinks-guide" className="home-page__cta home-page__cta--secondary">
            Science of THC
          </Link>
          <Link to="/thc-drinks?buyable=true" className="home-page__cta home-page__cta--buy">
            Buy THC Drinks
          </Link>
        </div>
        <LegalizationSection />
      </section>

      {/* How it works */}
      <section className="home-page__section">
        <h2 className="home-page__section-title">How It Works</h2>
        <div className="home-page__steps">
          <div className="home-page__step">
            <span className="home-page__step-icon">🔍</span>
            <h3>Search</h3>
            <p>Browse hundreds of THC beverages. Filter by potency, format, flavor, and effect.</p>
          </div>
          <div className="home-page__step">
            <span className="home-page__step-icon">⭐</span>
            <h3>Rate</h3>
            <p>Rate drinks 1–5 stars, score the buzz intensity (1–10), and choose the effect: Up, Down, or Neutral.</p>
          </div>
          <div className="home-page__step">
            <span className="home-page__step-icon">💬</span>
            <h3>Review</h3>
            <p>Share your experience with the community. Help others find their perfect drink.</p>
          </div>
          <div className="home-page__step">
            <span className="home-page__step-icon">🛒</span>
            <h3>Buy</h3>
            <p>Find the best price and buy directly from brands through our marketplace.</p>
          </div>
        </div>
      </section>

      {/* Rating system explained */}
      <section className="home-page__section home-page__section--alt">
        <h2 className="home-page__section-title">Our Rating System</h2>
        <div className="home-page__rating-info">
          <div className="home-page__rating-item">
            <span className="home-page__rating-label">★ Star Rating</span>
            <p>Overall quality on a 1–5 scale (half-stars supported)</p>
          </div>
          <div className="home-page__rating-item">
            <span className="home-page__rating-label">⚡ Buzz Score</span>
            <p>How strong is the effect? Rate intensity from 1 (mild) to 10 (strong)</p>
          </div>
          <div className="home-page__rating-item">
            <span className="home-page__rating-label">↑↓ Effect</span>
            <p>Energizing (Up), relaxing (Down), or somewhere in between (Neutral)?</p>
          </div>
        </div>
        <p className="home-page__note">
          Products need at least 3 ratings before aggregate scores are displayed.
        </p>
      </section>

      {/* My Stash / Collection */}
      <section className="home-page__section">
        <h2 className="home-page__section-title">Build Your Stash</h2>
        <p className="home-page__section-desc">
          Every drink you rate and review gets saved to your personal Stash — a visual collection of every THC beverage you've tried.
        </p>
        <div className="home-page__stash-grid">
          <div className="home-page__stash-card">
            <span className="home-page__stash-icon">📸</span>
            <h3>Drink Gallery</h3>
            <p>Your rated drinks appear as a visual gallery — see your entire collection at a glance.</p>
          </div>
          <div className="home-page__stash-card">
            <span className="home-page__stash-icon">🏅</span>
            <h3>Collection Status</h3>
            <p>Earn badges as you grow your stash. The more you review, the higher your tier.</p>
          </div>
          <div className="home-page__stash-card">
            <span className="home-page__stash-icon">📊</span>
            <h3>Track Progress</h3>
            <p>See how many reviews until your next milestone and unlock the next badge.</p>
          </div>
        </div>
        <div className="home-page__tiers">
          <span className="home-page__tier">🥉 Bronze <small>5+</small></span>
          <span className="home-page__tier">🥈 Silver <small>10+</small></span>
          <span className="home-page__tier">🥇 Gold <small>20+</small></span>
          <span className="home-page__tier">💎 Platinum <small>30+</small></span>
          <span className="home-page__tier">👑 Diamond <small>50+</small></span>
        </div>
        <div className="home-page__cta-row" style={{ justifyContent: 'center', marginTop: 'var(--spacing-md)' }}>
          <Link to="/stash" className="home-page__cta home-page__cta--primary">
            View My Stash
          </Link>
        </div>
      </section>

      {/* Featured Brands */}
      {featuredBrands.length > 0 && (
        <section className="home-page__section">
          <h2 className="home-page__section-title">Featured Brands</h2>
          <div className="home-page__brands">
            {featuredBrands.map((brand) => (
              <div key={brand.brandId} className="home-page__brand-card">
                {brand.logoUrl && (
                  <img
                    src={brand.logoUrl}
                    alt={brand.brandName}
                    className="home-page__brand-logo"
                  />
                )}
                <h3 className="home-page__brand-name">{brand.brandName}</h3>
                {brand.description && (
                  <p className="home-page__brand-desc">
                    {brand.description.length > 125
                      ? brand.description.slice(0, 125) + '...'
                      : brand.description}
                  </p>
                )}
                <Link
                  to={`/thc-drinks?brand=${encodeURIComponent(brand.brandName)}&buyable=true`}
                  className="home-page__cta home-page__cta--buy home-page__cta--sm"
                >
                  Learn More
                </Link>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* About Us */}
      <section className="home-page__section" style={{ textAlign: 'center' }}>
        <h2 className="home-page__section-title">About Us</h2>
        <p className="home-page__section-desc">
          Hygh Vybe is an informational and review platform that is not censored by THC manufacturers. Honest reviews by real people.
        </p>
        <div className="home-page__cta-row" style={{ justifyContent: 'center' }}>
          <Link to="/terms" className="home-page__cta home-page__cta--secondary">
            Terms and Policy
          </Link>
        </div>
      </section>

      {/* Community */}
      <section className="home-page__section" style={{ textAlign: 'center' }}>
        <h2 className="home-page__section-title">Join the Community</h2>
        <p className="home-page__section-desc">
          Create a free account to rate drinks, write reviews, and save your favorites.
        </p>
        <div className="home-page__cta-row" style={{ justifyContent: 'center' }}>
          <Link to="/auth/register" className="home-page__cta home-page__cta--primary">
            Sign Up Free
          </Link>
          <Link to="/auth/login" className="home-page__cta home-page__cta--secondary">
            Log in
          </Link>
        </div>
      </section>
    </div>
  );
}
